import React, { useState } from 'react';
import styled from 'styled-components';

interface Props {
  icon: React.ReactNode;
  title: string;
  description: string;
  justificativa?: string;
  /** true só no card AO VIVO (via SSE); reidratado do histórico vem sem → passivo. */
  active?: boolean;
  runLabel: string;
  declineLabel: string;
  decidedRunText: string;
  decidedDeclineText: string;
  passiveText: string;
  onDecide: (decision: 'run' | 'decline') => void;
  cardTestId?: string;
  runTestId?: string;
  declineTestId?: string;
}

/**
 * Card de confirmação dos sub-agentes (panorama / pesquisa aprofundada) —
 * mesma família visual do AgentProgressCard: chrome neutro, header com ícone
 * + título, ações com o acento verde. Ao vivo mostra os botões; decidido ou
 * reidratado do histórico vira um registro passivo.
 */
const AgentConfirmationCard: React.FC<Props> = ({
  icon,
  title,
  description,
  justificativa,
  active,
  runLabel,
  declineLabel,
  decidedRunText,
  decidedDeclineText,
  passiveText,
  onDecide,
  cardTestId,
  runTestId,
  declineTestId,
}) => {
  const [decided, setDecided] = useState<null | 'run' | 'decline'>(null);
  const showButtons = active === true && decided === null;

  const decide = (decision: 'run' | 'decline') => {
    if (decided) return;
    setDecided(decision);
    onDecide(decision);
  };

  return (
    <Card data-testid={cardTestId}>
      <Header>
        <IconWrap>{icon}</IconWrap>
        <Title>{title}</Title>
      </Header>
      <Description>{description}</Description>
      {justificativa ? <Justificativa>{justificativa}</Justificativa> : null}
      {showButtons ? (
        <Actions>
          <PrimaryButton data-testid={runTestId} onClick={() => decide('run')}>
            {runLabel}
          </PrimaryButton>
          <GhostButton data-testid={declineTestId} onClick={() => decide('decline')}>
            {declineLabel}
          </GhostButton>
        </Actions>
      ) : (
        <Outcome>
          {decided === 'run' ? decidedRunText : decided === 'decline' ? decidedDeclineText : passiveText}
        </Outcome>
      )}
    </Card>
  );
};

export default AgentConfirmationCard;

const Card = styled.div`
  border: 1px solid rgba(127, 127, 127, 0.28);
  border-radius: 12px;
  padding: 12px 16px 14px;
  margin: 8px 0;
  font-family: 'Sora', 'Lexend', sans-serif;
  color: ${({ theme }) => theme.font_color || 'inherit'};
  background: rgba(127, 127, 127, 0.04);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
`;

const IconWrap = styled.span`
  display: inline-flex;
  align-items: center;
  opacity: 0.8;
`;

const Title = styled.span`
  font-weight: 600;
  font-size: 0.92rem;
`;

const Description = styled.div`
  font-size: 0.84rem;
  opacity: 0.72;
  line-height: 1.45;
`;

const Justificativa = styled.div`
  font-size: 0.8rem;
  opacity: 0.55;
  font-style: italic;
  margin-top: 6px;
  line-height: 1.4;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 12px;
`;

const PrimaryButton = styled.button`
  padding: 7px 18px;
  border-radius: 8px;
  border: none;
  background: #047857;
  color: #fff;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.84rem;
  font-family: inherit;
  &:hover { background: #065f46; }
`;

const GhostButton = styled.button`
  padding: 7px 16px;
  border-radius: 8px;
  border: 1px solid rgba(127, 127, 127, 0.4);
  background: transparent;
  color: inherit;
  cursor: pointer;
  font-size: 0.84rem;
  font-family: inherit;
  &:hover { background: rgba(127, 127, 127, 0.08); }
`;

const Outcome = styled.div`
  font-size: 0.8rem;
  opacity: 0.55;
  margin-top: 10px;
`;
